// audioPlay.js
class audioPlay {
  constructor() {
    // Legacy lists (retain so existing code keeps working)
    this.audioList = {}; // legacy <audio> elements (fallback)
    this.upNextList = {}; // legacy sequence map
    this.upNextListJSON = null;
    this.upNextListJSONArray = null;
    this.element = 0;

    // Web Audio state
    this.ctx = null;
    this.masterGain = null;
    this.audioBuffers = {}; // url -> Promise<{buffer: AudioBuffer}>
    this.playingSources = {}; // url -> Set<BufferSourceNode> currently playing
    this.unlocking = false;
    this.unlocked = false;

    this._pcmRemainder = new Uint8Array(0);

    // === PCM streaming sink (controlled by VoiceService) === 
    this.pcm = {
      active: false, // true between tts_start and drained after tts_end
      endReceived: false,
      sampleRate: 24000, // default; ttsPcmStart can override
      scheduleTime: 0, // next start time in AudioContext
      sources: new Set(), // BufferSourceNodes scheduled for PCM
      driftPadSec: 0.25, // pad to fight jitter
      queuedSamples: 0, // VoiceService polls this to know when we're drained,

      floatQueue: [],        // Array<Float32Array>
      floatQueueSamples: 0,  // total samples buffered
      minBufferSec: 1.5,     // buffering threshold

      totalExpectedBytes: 0, // set by tts_end command from backend
      totalReceivedBytes: 0, // incremented by VoiceService on each chunk
      _completionTimer: null, // debounce timer for completion check
    };
    this.bindUnlockEvents();
  }

  /* ------------------------------------------------------------------
   *  PUBLIC: called once from genuine user gesture (tap)
   * ------------------------------------------------------------------ */
  initFromUserGesture = async () => {
	  if (this.unlocked || this.unlocking) return;
	  this.unlocking = true;

	  try {
		this.ctx = this.ctx || new (window.AudioContext || window.webkitAudioContext)();

		this.masterGain = this.masterGain || this.ctx.createGain();
		this.masterGain.gain.setValueAtTime(1, this.ctx.currentTime);
		this.masterGain.connect(this.ctx.destination);

		this.ctx.onstatechange = () => {
		  console.log("AudioContext state:", this.ctx.state);
		};

		// Resume first, and wait for it
		if (this.ctx.state !== "running") {
		  await this.ctx.resume();
		}

		// Then play a tiny unlock sound
		const buffer = this.ctx.createBuffer(1, Math.floor(this.ctx.sampleRate * 0.1), this.ctx.sampleRate);
		const data = buffer.getChannelData(0);
		for (let i = 0; i < data.length; i++) {
		  data[i] = 0.01 * Math.sin((2 * Math.PI * 220 * i) / this.ctx.sampleRate);
		}

		const src = this.ctx.createBufferSource();
		src.buffer = buffer;
		src.connect(this.masterGain);
		src.start();

		this.unlocked = (this.ctx.state === "running");
	  } catch (err) {
		console.error("Audio unlock failed:", err);
	  } finally {
		this.unlocking = false;
	  }
	};
	
  unlockAudioForChat = async () => {
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    if (!isIOS) return true;

    if (this.ctx && this.ctx.state === 'running') return true;

    try {
      // Playing the silent HTML5 audio for iso
      const silentAudio = new Audio();
      silentAudio.volume = 0;
      await silentAudio.play();

      // CreatingResuming AudioContext
      if (!this.ctx) {
        this.ctx = new (window.AudioContext || window.webkitAudioContext)();
        this.masterGain = this.ctx.createGain();
        this.masterGain.connect(this.ctx.destination);
      }

      if (this.ctx.state === 'suspended') {
        await this.ctx.resume();
      }

      //now  Playing  thesilent buffer to get it fully unlock
      const buffer = this.ctx.createBuffer(1, 1, 22050);
      const source = this.ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(this.masterGain);
      source.start(0);

      this.unlocked = true;
      console.log("[Audio] Unlocked for chat mode");
      return true;
    } catch (err) {
      console.error("[Audio] Failed to unlock:", err);
      return false;
    }
  };

  /* Ensure context is running before playback */
  ensureCtxRunning = async () => {
    if (!this.ctx) return;
    if (this.ctx.state === "suspended") {
      try {
        await this.ctx.resume();
      } catch (err) {
        console.warn("AudioContext resume failed:", err);
      }
    }
  };

  /* ------------------------------------------------------------------
   *  PRELOAD (fetch+decode) — for URL clips (mp3/ogg/wav). NOT used for PCM.
   * ------------------------------------------------------------------ */
  preloadAndUnlockAudio = (url) => {
    const resolvedUrl = this.getLocalAudio(url);
    if (this.audioBuffers[url]) return this.audioBuffers[url];

    this.audioBuffers[url] = (async () => {
      try {
        const resp = await fetch(resolvedUrl, { mode: "cors" });
        const arrayBuf = await resp.arrayBuffer();
        const audioBuf = await this.ctx.decodeAudioData(arrayBuf.slice(0)); // expects self-describing formats
        return { buffer: audioBuf, url, resolvedUrl };
      } catch (err) {
        console.error("Failed to preload", url, err);
        return { buffer: null, url, resolvedUrl, error: err };
      }
    })();

    // Legacy <audio> fallback element
    const audio = new Audio(resolvedUrl);
    audio.preload = "auto";
    audio.load();
    this.audioList[url] = audio;

    return this.audioBuffers[url];
  };

  preloadMultipleAudios = (audioUrls) => {
    audioUrls.forEach((url) => this.preloadAndUnlockAudio(url));
  };

  /* ------------------------------------------------------------------
   *  PLAY single clip by URL (your existing path)
   * ------------------------------------------------------------------ */
  playAudio = async (url) => {
    if (!url) {
      console.log("error, no audio");
      return false;
    }
    if (!this.unlocked) {
      console.warn("playAudio called before user unlock; ignoring.");
      return false;
    }
    await this.ensureCtxRunning();

    const entry = await this.preloadAndUnlockAudio(url);
    if (!entry || !entry.buffer) {
      console.warn("Falling back to <audio> element for", url);
      return this.playViaHtmlAudio(url);
    }

    const src = this.ctx.createBufferSource();
    src.buffer = entry.buffer;
    src.connect(this.masterGain);

    if (this.isInstructionClip(url)) {
      src.addEventListener("ended", this.recognizeAudioEnded, { once: true });
    }

    if (!this.playingSources[url]) this.playingSources[url] = new Set();
    this.playingSources[url].add(src);
    src.onended = () => {
      const set = this.playingSources[url];
      if (set) {
        set.delete(src);
        if (set.size === 0) delete this.playingSources[url];
      }
    };

    try {
      src.start(0);
      console.log("#---# PLAY (WebAudio)", url);
      return true;
    } catch (err) {
      console.error("WebAudio start failed, fallback <audio>:", err);
      return this.playViaHtmlAudio(url);
    }
  };

  playAudioAt = async (url, offsetSec = 0) => {
    if (!url || !this.unlocked) return false;

    await this.ensureCtxRunning();

    const entry = await this.preloadAndUnlockAudio(url);
    if (!entry || !entry.buffer) return false;

    const src = this.ctx.createBufferSource();
    src.buffer = entry.buffer;
    src.connect(this.masterGain);

    if (!this.playingSources[url]) this.playingSources[url] = new Set();
    this.playingSources[url].add(src);

    src.onended = () => {
      const set = this.playingSources[url];
      if (set) {
        set.delete(src);
        if (set.size === 0) delete this.playingSources[url];
      }
    };

    const now = this.ctx.currentTime;
    const startAt = now + 0.15; // small future scheduling buffer
    const offs = Math.max(0, offsetSec || 0);

    try {
      src.startTime = startAt;
      src.offset = offs;
      src.start(startAt, offs);
      console.log("Scheduled WebAudio at", startAt);

      return {
        success: true,
        scheduledTime: startAt
      };
    } catch (err) {
      console.error("WebAudio start failed:", err);
      return false;
    }
  };


  playViaHtmlAudio = (url) => {
    const resolvedUrl = this.getLocalAudio(url);
    if (!this.audioList[url]) this.audioList[url] = new Audio(resolvedUrl);
    const el = this.audioList[url];
    el.currentTime = 0;
    el.play().catch((err) => console.error("HTMLAudio play failed:", err));

    if (this.isInstructionClip(url)) {
      el.removeEventListener("ended", this.nextUpAudioEnded, false);
      el.removeEventListener("ended", this.recognizeAudioEnded, false);
      el.addEventListener("ended", this.recognizeAudioEnded, false);
    }
    return true;
  };

  isAudioPlaying = (url) => {
    const set = this.playingSources[url];
    if (!set || !this.ctx) return false;

    const now = this.ctx.currentTime;

    for (const src of set) {
      if (!src.startTime || !src.buffer) continue;

      const duration = src.buffer.duration - (src.offset || 0);
      const endTime = src.startTime + duration;

      // Considering it as of "playing" scheduled to start in the future
      if (now < src.startTime || (now >= src.startTime && now <= endTime)) {
        return true;
      }
    }

    return false;
  };

  /* ------------------------------------------------------------------
   * STOP (by URL or stop-all)
   * ------------------------------------------------------------------ */
  stopAudio = (url) => {
    console.log("#---# PAUSE", url);

    if (url) {
      if (this.playingSources[url]) {
        this.playingSources[url].forEach((src) => {
          try {
            src.stop();
          } catch (_) { }
        });
        delete this.playingSources[url];
      }
      const el = this.audioList[url];
      if (el && el.currentTime > 0) {
        el.pause();
        el.currentTime = 0;
        el.removeEventListener("ended", this.nextUpAudioEnded, false);
        el.removeEventListener("ended", this.recognizeAudioEnded, false);
      }
      if (window.disableRepTmer) {
        window.disableRepTmer();
      }
    } else {
      Object.keys(this.playingSources).forEach((k) => this.stopAudio(k));
      Object.keys(this.audioList).forEach((k) => this.stopAudio(k));
      this.upNextList = {};
      this.upNextListJSON = null;
      this.startingList = null;
      this.element = 100;
      // Also stop any streaming PCM
      this.ttsPcmAbort();
    }
  };

  /* ------------------------------------------------------------------
   * Sequence playback (unchanged)
   * ------------------------------------------------------------------ */
  upNextAudio = async (audioListJSON, backcall) => {
    if (!audioListJSON || this.element === 100) {
      console.log("error, no audio");
      return false;
    }

    if (!backcall) {
      this.upNextListJSON = audioListJSON;
      const stripped = audioListJSON.replace("[", "").replace("]", "");
      this.upNextListJSONArray = stripped.split(",").map((s) => s.trim());
      this.element = 0;
    }

    const url = this.upNextListJSONArray[this.element];
    if (!url) {
      console.warn("upNext: no url at element", this.element);
      return false;
    }

    await this.preloadAndUnlockAudio(url);
    await this.playAudio(url);
    this.attachSequenceEnded(url);
  };

  attachSequenceEnded = (url) => {
    const entryPromise = this.audioBuffers[url];
    if (!entryPromise) return;
    entryPromise.then((entry) => {
      if (!entry || !entry.buffer) return;
      const dur = entry.buffer.duration;
      setTimeout(() => this.nextUpAudioEnded(), dur * 1000 + 20);
    });
  };

  nextUpAudioEnded = () => {
    this.element++;
    if (!this.upNextListJSONArray || this.upNextListJSONArray.length <= this.element || this.element >= 100) {
      if (window.recognizeAudioEnded) {
        window.recognizeAudioEnded();
      }
    } else {
      this.upNextAudio(this.upNextListJSON, true);
    }
  };

  resetUpNextAudio = () => {
    this.element = 0;
    this.upNextList = {};
  };

  /* ------------------------------------------------------------------
   * INSTRUCTION DETECTOR (unchanged)
   * ------------------------------------------------------------------ */
  isInstructionClip = (url) => {
    const targets = ["instructions", "deadbug2", "birddog2", "supinebridge2", "superman2", "child_pose", "clam_shell_left", "clam_shell_right", "facestraight_apart", "side_plank_left", "side_plank_right", "supine_single_knee_chest", "full_plank", "plank_on_elbows", "silent", "standing_gastroc_stretch"];
    return targets.some((s) => url.indexOf(s) !== -1);
  };

  recognizeAudioEnded = () => {
    console.log("Instructions audio ended");
    if (window.recognizeInstructions) {
      window.recognizeInstructions();
    }
  };

  /* ------------------------------------------------------------------
   * === NEW: PCM sink used by VoiceService ===
   * VoiceService calls:
   *   ttsPcmStart(sampleRate?)
   *   ttsPcmPush(ArrayBuffer)  // raw 16-bit little-endian, mono
   *   ttsPcmEnd()
   *   ttsPcmAbort()
   * It also reads:
   *   pcm.active, pcm.queuedSamples
   * ------------------------------------------------------------------ */

  ttsPcmStart = (sampleRate) => {
    if (!this.unlocked) return;
    if (!this.ctx) this.initFromUserGesture();

    // 🔴 HARD STOP EVERYTHING FIRST
    this.stopAudio();          // stops URL playback
    this._pcmStopAll();        // stop scheduled PCM sources
    this.pcm.floatQueue.length = 0;
    this.pcm.floatQueueSamples = 0;
    this.pcm.queuedSamples = 0;
    this._pcmRemainder = new Uint8Array(0);

    // Reset byte verification counters
    this.pcm.totalExpectedBytes = 0;
    this.pcm.totalReceivedBytes = 0;
    if (this.pcm._completionTimer) {
      clearTimeout(this.pcm._completionTimer);
      this.pcm._completionTimer = null;
    }

    if (Number.isFinite(sampleRate) && sampleRate > 0) {
      this.pcm.sampleRate = sampleRate | 0;
    }

    const now = this.ctx.currentTime;

    // 🔴 Reset schedule time to NOW
    this.pcm.scheduleTime = now + 0.05;

    this.pcm.active = true;
    this.pcm.endReceived = false;

    console.log("[PCM] START (exclusive) @", this.pcm.sampleRate);
  };

  ttsPcmPush = (arrayBuffer) => {
    // Allow push if active OR if endReceived but bytes still expected
    if (!this.ctx) return;
    if (!this.pcm.active && !this.pcm.endReceived) return;

    const floats = this._pcm16leToFloat32(arrayBuffer);
    if (!floats.length) return;

    // Buffer the data
    this.pcm.floatQueue.push(floats);
    this.pcm.floatQueueSamples += floats.length;

    // If end was already received, force-flush immediately — these are late-arriving chunks
    if (this.pcm.endReceived) {
      console.log("[PCM] Late chunk after END received - force flushing", floats.length, "samples");
      this._flushPcmQueue(true);
    } else {
      // Normal path: let the scheduler decide based on minBufferSec threshold
      this._flushPcmQueue(false);
    }
  };


  _checkAndRecoverAudioContext = async () => {
    if (!this.ctx) {
      this.initFromUserGesture();
      return true;
    }

    const state = this.ctx.state;
    console.log("[AUDIO] Context state:", state);

    if (state === "running") {
      return true;
    }

    if (state === "suspended") {
      try {
        await this.ctx.resume();
        // Verifying that the resume actually works
        if (this.ctx.state === "running") {
          console.log("[AUDIO] Context resumed successfully");
          return true;
        }
      } catch (err) {
        console.error("[AUDIO] Resume failed:", err);
      }

      // If resume failed, create new context
      console.warn("[AUDIO] Creating new AudioContext");
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.masterGain = this.ctx.createGain();
      this.masterGain.connect(this.ctx.destination);
      return true;
    }
    return false;
  };
  bindUnlockEvents() {
    const unlock = async () => {
      if (this.unlocked) return;
      if (this.ctx) {
        await this._checkAndRecoverAudioContext();
        // Play a tiny silent buffer to "warm up" the hardware
        const buffer = this.ctx.createBuffer(1, 1, 22050);
        const source = this.ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(this.ctx.destination);
        source.start(0);

        // Remove listeners once unlocked
        ['touchstart', 'click', 'keydown'].forEach(evt =>
          document.removeEventListener(evt, unlock, true)
        );
        this.unlocked = true;
      }
    };
    ['touchstart', 'click', 'keydown'].forEach(evt =>
      document.addEventListener(evt, unlock, { capture: true, once: true })
    );
  };

  ttsPcmEnd = (totalExpectedBytes) => {
    if (!this.pcm.active) return;

    // Store expected byte count for verification
    if (totalExpectedBytes && totalExpectedBytes > 0) {
      this.pcm.totalExpectedBytes = totalExpectedBytes;
    }

    // Force flush remaining queued audio
    this._flushPcmQueue(true);

    this.pcm.endReceived = true;

    console.log(`[PCM] END (final audio flushed, expected bytes: ${this.pcm.totalExpectedBytes}, received: ${this.pcm.totalReceivedBytes})`);
  };


  ttsPcmAbort = () => {
    this._pcmStopAll();

    this.pcm.active = false;
    this.pcm.floatQueue.length = 0;
    this.pcm.floatQueueSamples = 0;
    this.pcm.queuedSamples = 0;
    this._pcmRemainder = new Uint8Array(0);

    if (this.ctx) {
      const now = this.ctx.currentTime;

      // 🔴 CRITICAL: reset timeline
      this.pcm.scheduleTime = now + 0.05;

      // 🔴 force silence gap
      const silence = this.ctx.createBuffer(1, 128, this.ctx.sampleRate);
      const s = this.ctx.createBufferSource();
      s.buffer = silence;
      s.connect(this.masterGain);
      s.start(now);
    }

    console.log("[PCM] ABORT (hard reset timeline)");
  };



  _pcm16leToFloat32 = (arrayBuffer) => {
    let input = new Uint8Array(arrayBuffer);

    // Prepend remainder from last chunk
    if (this._pcmRemainder.length) {
      const combined = new Uint8Array(
        this._pcmRemainder.length + input.length
      );
      combined.set(this._pcmRemainder, 0);
      combined.set(input, this._pcmRemainder.length);
      input = combined;
      this._pcmRemainder = new Uint8Array(0);
    }

    // Only consume even byte count
    const usableBytes = input.length & ~1;
    if (usableBytes !== input.length) {
      this._pcmRemainder = input.slice(usableBytes);
      input = input.slice(0, usableBytes);
    }

    const view = new DataView(input.buffer, input.byteOffset, input.byteLength);
    const samples = input.byteLength >> 1;
    const out = new Float32Array(samples);

    for (let i = 0; i < samples; i++) {
      const s = view.getInt16(i << 1, true);
      out[i] = s < 0 ? s / 32768 : s / 32767;
    }

    return out;
  };

  _flushPcmQueue = (force = false) => {
    if (!this.ctx || !this.pcm.floatQueueSamples) return;

    const bufferedSec = this.pcm.floatQueueSamples / this.pcm.sampleRate;

    // Do nothing unless forced or enough buffered
    if (!force && bufferedSec < this.pcm.minBufferSec) return;

    const frames = this.pcm.floatQueueSamples;
    const merged = new Float32Array(frames);

    // console.log("[PCM] scheduled", frames, "frames", "Forced", force);

    let offset = 0;
    for (const chunk of this.pcm.floatQueue) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }

    // Clear queue FIRST (important)
    this.pcm.floatQueue.length = 0;
    this.pcm.floatQueueSamples = 0;

    // Optional: soft fade to prevent clicks
    const fade = Math.min(128, frames >> 2);
    for (let i = 0; i < fade; i++) {
      const g = i / fade;
      merged[i] *= g;
      merged[frames - 1 - i] *= g;
    }

    const buf = this.ctx.createBuffer(1, frames, this.pcm.sampleRate);
    buf.copyToChannel(merged, 0);

    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.masterGain);

    const now = this.ctx.currentTime;

    // Clamp scheduleTime to avoid underruns
    if (this.pcm.scheduleTime < now + 0.05) {
      this.pcm.scheduleTime = now + 0.05;
    }

    const startAt = this.pcm.scheduleTime;
    src.start(startAt);

    this.pcm.sources.add(src);
    this.pcm.queuedSamples += frames;

    src.onended = () => {
      this.pcm.sources.delete(src);
      this.pcm.queuedSamples = Math.max(0, this.pcm.queuedSamples - frames);
      console.log(`[PCM] Source ended, remaining sources: ${this.pcm.sources.size}, queued: ${this.pcm.queuedSamples}`);

      // Debounced completion check — wait 300ms to allow late chunks to arrive
      if (this.pcm.endReceived && this.pcm.sources.size === 0 && this.pcm.queuedSamples === 0) {
        if (this.pcm._completionTimer) clearTimeout(this.pcm._completionTimer);
        this.pcm._completionTimer = setTimeout(() => {
          // Re-check after debounce — a late chunk may have arrived and scheduled new audio
          if (this.pcm.sources.size === 0 && this.pcm.queuedSamples === 0) {
            // Byte verification: if backend sent totalBytes, confirm we got everything
            const bytesOk = this.pcm.totalExpectedBytes === 0 || this.pcm.totalReceivedBytes >= this.pcm.totalExpectedBytes;
            if (bytesOk) {
              console.log(`[PCM] All playback complete - deactivating (received: ${this.pcm.totalReceivedBytes}, expected: ${this.pcm.totalExpectedBytes})`);
              this.pcm.active = false;
              this.pcm.endReceived = false;
            } else {
              console.warn(`[PCM] Sources drained but bytes mismatch - received: ${this.pcm.totalReceivedBytes}, expected: ${this.pcm.totalExpectedBytes}. Waiting...`);
            }
          }
        }, 300);
      }
    };

    this.pcm.scheduleTime = startAt + buf.duration;
  };


  _pcmStopAll = () => {
    this.pcm.sources.forEach((src) => {
      try {
        src.onended = null;
        src.disconnect();
        src.stop(0);
      } catch (_) { }
    });

    this.pcm.sources.clear();
  };


  /* ------------------------------------------------------------------
   * LEGACY HOOKUP (window.* shims you already rely on)
   * ------------------------------------------------------------------ */
  listenNative = () => {
    window.playAudio = this.playAudio;
    window.stopAudio = this.stopAudio;
    window.upNextAudio = this.upNextAudio;
    window.resetUpNextAudio = this.resetUpNextAudio;

    // expose unlock + ensure
    window.initFromUserGesture = this.initFromUserGesture;
    window.preloadMultipleAudios = this.preloadMultipleAudios;

    // expose PCM sink for VoiceService
    window.audioPlayer = this; // make sure global points here (if not already)
    // (VoiceService accesses: window.audioPlayer.pcm, .ttsPcmStart/.Push/.End/.Abort)

    window.preloadAllAudios = () => {
      // IMPORTANT: unlock context first!
      this.initFromUserGesture();

      if (window.audiosPreloadedAll) return;
      window.audiosPreloadedAll = true;

      const importantSounds = ["https://virdiodev.s3.us-east-2.amazonaws.com/virtual_audio/CameraSetupStartFreePTv1.0.mp3", "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_audio/CameraSetupVirdioHealthv1.0.mp3", "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_audio/success_sound1.mp3", "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_audio/success_sound1.mp3?v=6423234", "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_audio/success_sound2.mp3", "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_audio/success_sound2.mp3?v=6423234", "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_audio/start_exerc.mp3", "https://virdiodev.s3.us-east-2.amazonaws.com/virtual_audio/virtual_210.mp3"];

      for (let i = 1; i <= 11; i++) {
        importantSounds.push(`https://virdiodev.s3.us-east-2.amazonaws.com/virtual_audio/countdown_${i}_0.mp3`);
      }
      importantSounds.push(`https://virdiodev.s3.us-east-2.amazonaws.com/virtual_audio/countdown_10_1.mp3`);
      importantSounds.push(`https://virdiodev.s3.us-east-2.amazonaws.com/virtual_audio/countdown_4_1.mp3`);

      this.preloadMultipleAudios(importantSounds);
    };

    // Optional helper used elsewhere in your app
    window.videoUnlockPlay = () => {

    };
  };

  /* ------------------------------------------------------------------
   * LOCAL PATH RESOLUTION (Electron support, unchanged)
   * ------------------------------------------------------------------ */
  getLocalAudio(url_link) {
    console.log("URL LINK: ", url_link);
    url_link = url_link.split("?")[0].trim();
    if (!window["electron"]) return url_link;

    const fileName = url_link.split("/").pop().split("?")[0];
    try {
      window["fsFile"].accessSync(window["applicationPath"] + "/build/audio/" + fileName);
      return "./audio/" + fileName;
    } catch (err) {
      console.log("AUDIO NOT FOUND 1", url_link);
    }

    try {
      window["fsFile"].accessSync(window["applicationPath"] + "/audio/" + fileName);
      return "./audio/" + fileName;
    } catch (err) {
      console.log("AUDIO NOT FOUND 2", url_link);
    }

    return url_link;
  }

  ensurePcmWorkletLoaded = async () => {
    if (!this.ctx || this._pcmWorkletLoaded) return true;

    await this.ensureCtxRunning();
    await this.ctx.audioWorklet.addModule("/pcm-processor.js");

    this._pcmWorkletLoaded = true;
    return true;
  };

  startMicPcmCapture = async (stream, onPcm) => {
    if (!this.ctx || !this.unlocked) {
      console.warn("[PCM] Context not ready");
      return false;
    }

    await this.ensureCtxRunning();
    await this.ensurePcmWorkletLoaded();

    this._micSource = this.ctx.createMediaStreamSource(stream);
    this._pcmNode = new AudioWorkletNode(this.ctx, "pcm-processor", {
      processorOptions: {
        sampleRate: this.ctx.sampleRate,
      },
    });

    this._pcmNode.port.onmessage = (e) => {
      onPcm(e.data); // Int16Array buffer
    };

    this._micSource.connect(this._pcmNode);

    console.log("[PCM] Mic capture started");
    return true;
  };

  stopMicPcmCapture = () => {
    try {
      if (this._micSource) { this._micSource.disconnect(); }
      if (this._pcmNode) { this._pcmNode.disconnect(); }
    } catch (_) { }

    this._micSource = null;
    this._pcmNode = null;

    console.log("[PCM] Mic capture stopped");
  };




  // no-op legacy
  preloadAudioFiles = () => { };
}

export default audioPlay;
